import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { countryCodes } from '../data/mock';
import { toast } from 'sonner';
import { getGeoLocation, submitLead, getDialCodeFromCountry } from '../utils/api';

const PopupForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    message: ''
  });
  const [phoneError, setPhoneError] = useState('');
  const [geoLocationData, setGeoLocationData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Fetch geolocation on mount
    const fetchGeoLocation = async () => {
      const geoData = await getGeoLocation();
      if (geoData) {
        setGeoLocationData(geoData);
        const dialCode = getDialCodeFromCountry(geoData.countryCode);
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode,
          country: geoData.countryCode
        }));
      }
    };
    fetchGeoLocation();
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, '');
    const limitedValue = numericValue.slice(0, 11);
    
    setFormData({ ...formData, phone: limitedValue });
    
    if (numericValue.length > 11) {
      setPhoneError('Maximum 11 digits allowed');
    } else if (value !== numericValue) {
      setPhoneError('Only numbers are allowed');
    } else {
      setPhoneError('');
    }
  };

  const handleCountryCodeChange = (value) => {
    const selectedCountry = countryCodes.find(c => c.code === value);
    setFormData({ 
      ...formData, 
      countryCode: value,
      country: selectedCountry?.countryCode || 'US'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone && (formData.phone.length > 11 || !/^\d+$/.test(formData.phone))) {
      setPhoneError('Please enter a valid phone number (max 11 digits)');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await submitLead(formData, geoLocationData);
      
      if (result.statusCode === 200 && result.data.data?.customerId) {
        toast.success('Thank you! Redirecting...');
        setFormData({ name: '', email: '', countryCode: '+1', country: 'US', phone: '', message: '' });
        setPhoneError('');
        onClose();
        // Redirect to application page
        window.location.href = `/application/main?customerid=${result.data.data.customerId}`;
      } else if (result.statusCode === 401) {
        toast.error(result.message || 'Unauthorized access');
      } else if (result.statusCode === 400) {
        toast.error(result.data?.errorMessage || result.message || 'Invalid data provided');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-primary p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">Get Started Today</h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="text-secondary-100 mt-2">Fill out the form and we'll contact you shortly</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
            <Input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <Input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
            <div className="flex space-x-2">
              <Select
                value={formData.countryCode}
                onValueChange={handleCountryCodeChange}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.code} {item.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="tel"
                required
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="Phone number"
                className={`flex-1 ${phoneError ? 'border-red-500' : ''}`}
                maxLength={11}
              />
            </div>
            {phoneError && (
              <p className="text-red-500 text-sm mt-1">{phoneError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your trademark needs..."
              rows={4}
              className="w-full"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PopupForm;
